import styled from 'styled-components'
import { FormattedMessage, useIntl } from 'react-intl'
import { strings } from './BasicEventForm.strings'
import { Button, Card, DatePicker, Input } from '../../atoms'
import { useForm, Controller } from 'react-hook-form'
import { CategorySelector } from '../../molecules'
import { EventCategory } from '@/shared/types'

const StyledCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1em;
  max-width: 600px;
  width: 100%;
  > p {
    color: ${({ theme }) => theme.colors.font.subdued};
  }
`
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1em;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
  > p {
    margin: 0;
  }
`

interface BasicEventData {
  title: string
  date: string
  category: EventCategory
  otherCategory?: string
  isFeedPublic: boolean
}

interface Props {
  initialData?: BasicEventData
  onSubmit: (data: BasicEventData) => void
  isLoading?: boolean
}

export const BasicEventForm: React.FC<Props> = ({
  initialData,
  onSubmit,
  isLoading,
}) => {
  const intl = useIntl()
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BasicEventData>({
    defaultValues: initialData,
  })

  const defaultCategory = 'wedding' as const
  const submit = handleSubmit((data) => {
    onSubmit({
      ...data,
      isFeedPublic: true,
      // Category is a control and the default value isn't submitted so need to give provide it in case
      // the user never selects one outside of the default
      category: data.category ?? defaultCategory,
    })
  })

  return (
    <StyledCard>
      <Header>
        <h1>
          <FormattedMessage {...strings.header} />
        </h1>
        <p>
          <FormattedMessage {...strings.subheader} />
        </p>
      </Header>
      <Form onSubmit={submit} data-testid="form">
        <Input
          label={intl.formatMessage(strings.eventNameLabel)}
          error={
            errors.title &&
            intl.formatMessage(strings.errorLabel, {
              field: intl.formatMessage(strings.eventName),
            })
          }
          data-testid="BasicEventForm__Input_title"
          {...register('title')}
          required
        />
        {/* TODO: Write required validation */}
        <Controller
          name="date"
          control={control}
          render={({ field }) => (
            <DatePicker
              initialValue={initialData?.date}
              label={intl.formatMessage(strings.eventDateLabel)}
              data-testid="BasicEventForm__Input_date"
              minValue={new Date().toISOString()}
              error={intl.formatMessage(strings.dateError)}
              {...field}
              required
            />
          )}
        />
        <Controller
          name="category"
          control={control}
          render={({ field }) => (
            <>
              <CategorySelector
                onSelect={field.onChange}
                defaultValue={initialData?.category ?? defaultCategory}
              />
              {field.value === 'other' ? (
                <Input
                  label={intl.formatMessage(strings.otherCategoryLabel)}
                  error={
                    errors.otherCategory &&
                    intl.formatMessage(strings.errorLabel, {
                      field: intl.formatMessage(strings.otherCategory),
                    })
                  }
                  {...register('otherCategory')}
                />
              ) : null}
            </>
          )}
        />
        <Button disabled={isLoading}>
          <FormattedMessage {...strings.nextButton} />
        </Button>
      </Form>
    </StyledCard>
  )
}
